import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  createMainSideNavOverlaySlice,
  type MainSideNavOverlaySlice,
} from '~/components/v2/store/features/workspace/slices/createMainSideNavOverlaySlice';
import {
  createMainSideNavPinSlice,
  type MainSideNavPinSlice,
} from '~/components/v2/store/features/workspace/slices/createMainSideNavPinSlice';
import { useWorkspaceNotificationsStore } from '~/components/v2/store/features/workspace/useWorkspaceNotificationsStore';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';

const MAIN_SIDE_NAV_STORE_VERSION = 1;

export type MainSideNavState = MainSideNavPinSlice & MainSideNavOverlaySlice;

export const useMainSideNavStore = create<MainSideNavState>()(
  persist(
    (...args) => ({
      ...createMainSideNavPinSlice({ pinned: false })(...args),
      ...createMainSideNavOverlaySlice({ activeOverlay: null })(...args),
    }),
    {
      name: ZustandStorageKeys.MainSideNav,
      partialize: (state) => ({
        pinned: state.pinned,
      }),
      version: MAIN_SIDE_NAV_STORE_VERSION,
    },
  ),
);

useMainSideNavStore.subscribe((state, previousState) => {
  if (state.activeOverlay === previousState.activeOverlay) {
    return;
  }

  const shouldOpenNotifications = state.activeOverlay === 'notifications';

  if (useWorkspaceNotificationsStore.getState().open !== shouldOpenNotifications) {
    useWorkspaceNotificationsStore.getState().setOpen(shouldOpenNotifications);
  }
});

useWorkspaceNotificationsStore.subscribe((state, previousState) => {
  if (state.open === previousState.open) {
    return;
  }

  const { activeOverlay, setActiveOverlay } = useMainSideNavStore.getState();

  if (state.open && activeOverlay !== 'notifications') {
    setActiveOverlay('notifications');
  }

  if (!state.open && activeOverlay === 'notifications') {
    setActiveOverlay(null);
  }
});
